import { DeleteOutlined, UserAddOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import { Fragment, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectTeams } from 'reduxStore/teams/selectors';
import { teamsActions } from 'reduxStore/teams/slice';
import { Button, Col, Flex, List, Modal, Row, Select, Typography } from 'zui/Antd';
import { ConditionalTooltipButton } from 'zui/Custom';

const { Title, Text } = Typography;

const AddPersonToTeamModal = ({ open, setOpen, children, personID }) => {
	// ✅ Hooks
	const dispatch = useDispatch();
	const {
		token: {
			fontSizeSM,
			marginLG,
			paddingXL,
			padding,
			paddingLG,
			paddingXS,
			paddingMD,
			colorTextDescription,
			colorText,
			paddingSM
		}
	} = theme.useToken();

	// ✅ Redux State: Get all teams
	const teams = useSelector(selectTeams);
	const { loadingAddMembersTeam, completeAddMembersTeam } = useSelector((state) => state.Teams);

	// ✅ Local State
	const [internalVisible, setInternalVisible] = useState(false);
	const [selectedTeams, setSelectedTeams] = useState([]);

	const visible = open ?? internalVisible;
	const setVisible = setOpen ?? setInternalVisible;

	// ✅ Close modal
	const handleCancel = useCallback(() => {
		setSelectedTeams([]);
		setVisible(false);
	}, [setVisible]);

	// ✅ Close modal when adding people to teams is successful
	useEffect(() => {
		if (completeAddMembersTeam) {
			handleCancel();
		}
	}, [completeAddMembersTeam, handleCancel]);

	// ✅ Handle adding people to selected teams
	const handleAddPeopleToTeams = useCallback(() => {
		selectedTeams.forEach((team) => {
			dispatch(
				teamsActions.addTeamMember({
					teamID: team.teamID,
					personIDs: [personID]
				})
			);
		});
	}, [dispatch, selectedTeams, personID]);

	// ✅ Handle team selection
	const handleSelectTeam = (teamID) => {
		const selectedTeam = teams?.find((team) => team.teamID === teamID);
		if (selectedTeam && !selectedTeams.some((t) => t.teamID === teamID)) {
			setSelectedTeams([...selectedTeams, selectedTeam]);
		}
	};

	// ✅ Handle removing a team
	const handleRemoveTeam = (teamID) => {
		setSelectedTeams(selectedTeams.filter((team) => team.teamID !== teamID));
	};

	return (
		<Fragment>
			{/* 🔥 Trigger Button */}
			<ConditionalTooltipButton
				buttonText={'Add Person'}
				condition={false}
				disabledCondition={false}
				trueTitle={"You don't have permission"}
				falseTitle={"Add this Person's to Teams"}
				onClick={() => setVisible(true)}
				icon={<UserAddOutlined />}
			>
				{children}
			</ConditionalTooltipButton>

			{/* 🔥 Modal */}
			<Modal
				open={visible}
				onCancel={handleCancel}
				closeIcon={false}
				centered
				destroyOnClose
				footer={[
					<Flex
						align='center'
						justify='flex-end'
						style={{ marginTop: marginLG }}
						gap={paddingSM}
						key='footer'
					>
						<Button onClick={handleCancel} type='default' disabled={loadingAddMembersTeam}>
							Cancel
						</Button>
						<Button
							onClick={handleAddPeopleToTeams}
							type='primary'
							loading={loadingAddMembersTeam}
							disabled={selectedTeams.length === 0}
						>
							Add Person
						</Button>
					</Flex>
				]}
				width={472}
				styles={{ content: { padding: paddingXL } }}
			>
				{/* ✅ Modal Content */}
				<Row gutter={[paddingLG, paddingLG]} align='middle'>
					<Col span={24}>
						<Title level={4} style={{ margin: 0 }}>
							Add Person to Teams
						</Title>
						<Text style={{ fontSize: fontSizeSM, color: colorTextDescription }}>
							Assigning a person to a grants them all the permissions <br />
							associated with that team.
						</Text>
					</Col>

					{/* 🔥 Selected Teams */}
					<Col span={24}>
						<Flex vertical gap={padding}>
							<Text strong>Teams</Text>
							<Select
								showSearch
								placeholder='Enter or Select Team Name'
								style={{ width: '100%' }}
								options={teams
									.filter(
										(team) => !selectedTeams.some((t) => t.teamID === team.teamID) // ✅ FIX aquí
									)
									.map((team) => ({
										label: team.name,
										value: team.teamID
									}))}
								onSelect={handleSelectTeam} // ✅ When a user selects a team
								filterOption={(input, option) =>
									option.label.toLowerCase().includes(input.toLowerCase())
								}
								value={null}
							/>
						</Flex>
					</Col>

					{/* 🔥 Render Selected Teams */}
					<Col span={24}>
						<Flex vertical gap={paddingMD}>
							<Flex align='center' justify='space-between'>
								<Text strong>Selected Teams</Text>
								<Button
									type='text'
									onClick={() => setSelectedTeams([])}
									style={{ textDecoration: 'underline' }}
								>
									Clear List
								</Button>
							</Flex>

							<List
								dataSource={selectedTeams}
								loading={loadingAddMembersTeam}
								style={{
									minHeight: 186
								}}
								renderItem={(team) => (
									<List.Item
										style={{ padding: 0, border: 0, marginBottom: paddingXS }}
										extra={
											<Button
												icon={<DeleteOutlined style={{ fontSize: 16 }} />}
												onClick={() => handleRemoveTeam(team.teamID)}
												type='text'
												style={{ color: colorText }}
											/>
										}
									>
										<List.Item.Meta
											avatar={null}
											title={<Text style={{ fontWeight: 400 }}>{team.name}</Text>}
										/>
									</List.Item>
								)}
							/>
						</Flex>
					</Col>
				</Row>
			</Modal>
		</Fragment>
	);
};

export default AddPersonToTeamModal;
